import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Divider,
  IconButton,
  Snackbar,
} from '@mui/material';
import { ArrowBack, CheckCircle, Cancel, RateReview } from '@mui/icons-material';
import AdminLayout from '../../components/admin/AdminLayout';
import { apiClient, API_ENDPOINTS } from '../../config/api';

// ─── Design tokens ────────────────────────────────────────────────
const PINK  = '#EC4899';
const CORAL = '#FF6B35';

// ─── Types ────────────────────────────────────────────────────────
interface ProfileReviewDetail {
  id: number;
  userId: number;
  userNickname?: string;
  fieldTypes: string[];
  requestedAt: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  // 현재 승인된 데이터
  currentPhotos?: string[];
  currentJobTitle?: string;
  currentOneLiner?: string;
  currentSelfIntro?: string;
  // 심사 요청 데이터
  pendingPhotos?: string[];
  pendingJobTitle?: string;
  pendingOneLiner?: string;
  pendingSelfIntro?: string;
}

const FIELD_TYPE_LABELS: Record<string, string> = {
  PHOTOS:    '사진',
  JOB:       '직업',
  ONE_LINER: '한줄소개',
  SELF_INTRO:'자기소개',
};

// ─── Sub-component: Text comparison row ───────────────────────────
function TextCompareRow({ label, current, pending }: { label: string; current?: string; pending?: string }) {
  if (!pending) return null;
  const changed = current !== pending;
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
        {label}
        {changed && (
          <Chip label="변경됨" size="small" sx={{ ml: 1, bgcolor: '#FFF3E0', color: '#BF360C', fontSize: '0.68rem', height: 20 }} />
        )}
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
        <Box sx={{ p: 2, bgcolor: '#F5F5F5', borderRadius: 2, border: '1px solid #E0E0E0' }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>현재</Typography>
          <Typography variant="body2">{current || '(없음)'}</Typography>
        </Box>
        <Box sx={{ p: 2, bgcolor: changed ? '#FFF8E1' : '#F5F5F5', borderRadius: 2, border: `1px solid ${changed ? '#FFE082' : '#E0E0E0'}` }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>변경 요청</Typography>
          <Typography variant="body2" fontWeight={changed ? 600 : 400}>{pending || '(없음)'}</Typography>
        </Box>
      </Box>
    </Box>
  );
}

// ─── Component ────────────────────────────────────────────────────
export default function AdminProfileReviewDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const reviewId = Number(id);

  const [detail, setDetail]   = useState<ProfileReviewDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);

  // Approve dialog
  const [approveOpen, setApproveOpen]     = useState(false);
  const [approving, setApproving]         = useState(false);

  // Reject dialog
  const [rejectOpen, setRejectOpen]       = useState(false);
  const [rejectReason, setRejectReason]   = useState('');
  const [rejecting, setRejecting]         = useState(false);

  // Toast
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    const fetchDetail = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiClient.get<{ result: ProfileReviewDetail; resultType: string }>(
          API_ENDPOINTS.ADMIN.PROFILE_REVIEWS.DETAIL(reviewId)
        );
        if (res.resultType !== 'SUCCESS') throw new Error('상세 조회 실패');
        setDetail(res.result);
      } catch (e: any) {
        setError(e.message || '오류가 발생했습니다.');
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
  }, [reviewId]);

  const handleApprove = async () => {
    setApproving(true);
    try {
      await apiClient.post(API_ENDPOINTS.ADMIN.PROFILE_REVIEWS.APPROVE(reviewId));
      setToast('승인 완료');
      setApproveOpen(false);
      setTimeout(() => navigate('/admin/profile-reviews'), 1200);
    } catch (e: any) {
      setError(e.message || '승인 처리 중 오류가 발생했습니다.');
      setApproveOpen(false);
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) return;
    setRejecting(true);
    try {
      await apiClient.post(API_ENDPOINTS.ADMIN.PROFILE_REVIEWS.REJECT(reviewId), { rejectReason: rejectReason.trim() });
      setToast('거절 완료');
      setRejectOpen(false);
      setTimeout(() => navigate('/admin/profile-reviews'), 1200);
    } catch (e: any) {
      setError(e.message || '거절 처리 중 오류가 발생했습니다.');
      setRejectOpen(false);
    } finally {
      setRejecting(false);
    }
  };

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });

  return (
    <AdminLayout>
      <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <IconButton onClick={() => navigate('/admin/profile-reviews')} sx={{ mr: 0.5 }}>
            <ArrowBack />
          </IconButton>
          <Box sx={{
            width: 40, height: 40, borderRadius: '12px',
            background: `linear-gradient(135deg, ${PINK}, ${CORAL})`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <RateReview sx={{ fontSize: 20, color: '#fff' }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={800}>프로필 심사 상세</Typography>
            {detail && (
              <Typography variant="body2" color="text.secondary">
                심사 #{detail.id} · 사용자 {detail.userId}{detail.userNickname ? ` (${detail.userNickname})` : ''} · {formatDate(detail.requestedAt)}
              </Typography>
            )}
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Box sx={{ textAlign: 'center', py: 8 }}><CircularProgress /></Box>
        ) : detail ? (
          <>
            {/* 수정 필드 요약 */}
            <Card sx={{ p: 3, borderRadius: 3, mb: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>심사 항목</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {detail.fieldTypes.map((ft) => (
                  <Chip
                    key={ft}
                    label={FIELD_TYPE_LABELS[ft] ?? ft}
                    sx={{ bgcolor: '#FFF3E0', color: '#BF360C', fontWeight: 700 }}
                  />
                ))}
              </Box>
            </Card>

            {/* 사진 비교 */}
            {detail.fieldTypes.includes('PHOTOS') && (
              <Card sx={{ p: 3, borderRadius: 3, mb: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>사진 비교</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
                  {/* 현재 사진 */}
                  <Box>
                    <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ mb: 1 }}>
                      현재 사진
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
                      {(detail.currentPhotos ?? []).map((url, i) => (
                        <Box
                          key={i}
                          component="img"
                          src={url}
                          alt={`현재 ${i + 1}`}
                          sx={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: 2, border: '1px solid #E0E0E0' }}
                        />
                      ))}
                      {(detail.currentPhotos ?? []).length === 0 && (
                        <Typography variant="body2" color="text.secondary">(없음)</Typography>
                      )}
                    </Box>
                  </Box>
                  {/* 요청 사진 */}
                  <Box>
                    <Typography variant="body2" color="warning.dark" fontWeight={600} sx={{ mb: 1 }}>
                      변경 요청 사진
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
                      {(detail.pendingPhotos ?? []).map((url, i) => (
                        <Box
                          key={i}
                          component="img"
                          src={url}
                          alt={`요청 ${i + 1}`}
                          sx={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: 2, border: '2px solid #FFE082' }}
                        />
                      ))}
                      {(detail.pendingPhotos ?? []).length === 0 && (
                        <Typography variant="body2" color="text.secondary">(없음)</Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Card>
            )}

            {/* 텍스트 필드 비교 */}
            {(detail.fieldTypes.includes('JOB') || detail.fieldTypes.includes('ONE_LINER') || detail.fieldTypes.includes('SELF_INTRO')) && (
              <Card sx={{ p: 3, borderRadius: 3, mb: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>텍스트 필드 비교</Typography>
                <TextCompareRow label="직업" current={detail.currentJobTitle} pending={detail.pendingJobTitle} />
                <TextCompareRow label="한줄소개" current={detail.currentOneLiner} pending={detail.pendingOneLiner} />
                <TextCompareRow label="자기소개" current={detail.currentSelfIntro} pending={detail.pendingSelfIntro} />
              </Card>
            )}

            <Divider sx={{ my: 3 }} />

            {/* Actions */}
            {detail.status === 'PENDING' && (
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Cancel />}
                  onClick={() => setRejectOpen(true)}
                  sx={{ minWidth: 120, fontWeight: 700 }}
                >
                  거절
                </Button>
                <Button
                  variant="contained"
                  startIcon={<CheckCircle />}
                  onClick={() => setApproveOpen(true)}
                  sx={{ minWidth: 120, fontWeight: 700, background: `linear-gradient(135deg, ${PINK}, ${CORAL})` }}
                >
                  승인
                </Button>
              </Box>
            )}
            {detail.status !== 'PENDING' && (
              <Alert severity={detail.status === 'APPROVED' ? 'success' : 'error'}>
                {detail.status === 'APPROVED' ? '이미 승인된 심사 건입니다.' : '이미 거절된 심사 건입니다.'}
              </Alert>
            )}
          </>
        ) : null}
      </Box>

      {/* Approve Dialog */}
      <Dialog open={approveOpen} onClose={() => !approving && setApproveOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle fontWeight={700}>승인 확인</DialogTitle>
        <DialogContent>
          <Typography>이 프로필 수정 요청을 승인하시겠습니까?<br />승인 후 즉시 반영되며 사용자에게 알림이 발송됩니다.</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setApproveOpen(false)} disabled={approving}>취소</Button>
          <Button
            variant="contained"
            onClick={handleApprove}
            disabled={approving}
            startIcon={approving ? <CircularProgress size={16} color="inherit" /> : <CheckCircle />}
            sx={{ background: `linear-gradient(135deg, ${PINK}, ${CORAL})`, fontWeight: 700 }}
          >
            {approving ? '처리 중...' : '승인'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectOpen} onClose={() => !rejecting && setRejectOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle fontWeight={700}>거절 사유 입력</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>거절 사유를 입력해주세요. 사용자에게 전달됩니다.</Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="거절 사유"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="예: 사진이 불명확합니다. 정면 사진으로 다시 제출해주세요."
            disabled={rejecting}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => { setRejectOpen(false); setRejectReason(''); }} disabled={rejecting}>취소</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleReject}
            disabled={rejecting || !rejectReason.trim()}
            startIcon={rejecting ? <CircularProgress size={16} color="inherit" /> : <Cancel />}
            sx={{ fontWeight: 700 }}
          >
            {rejecting ? '처리 중...' : '거절'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast */}
      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={2000}
        onClose={() => setToast(null)}
        message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </AdminLayout>
  );
}
